import { api } from '@invisoutlet/shared/api';
import { useAuthContext } from '@invisoutlet/shared/context';
import type { Building, Entity, Stay } from '@invisoutlet/shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BuildingSearchDropdown } from '../components/building-search-dropdown.js';
import { EnterpriseLayout } from '../components/enterprise-layout.js';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toLocalDateString(isoString: string): string {
  return isoString.slice(0, 10);
}

function toLocalTimeString(isoString: string): string {
  return isoString.slice(11, 16);
}

function buildIso(date: string, time: string): string {
  return `${date}T${time}:00`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function firstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function isoToYMD(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return { y, m: m - 1, d };
}

function calendarDayKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function stayOverlapsDay(stay: Stay, year: number, month: number, day: number): boolean {
  const dayStr = calendarDayKey(year, month, day);
  const checkInDay = toLocalDateString(stay.checkIn);
  const checkOutDay = toLocalDateString(stay.checkOut);
  return dayStr >= checkInDay && dayStr <= checkOutDay;
}

function isStayStart(stay: Stay, year: number, month: number, day: number): boolean {
  return toLocalDateString(stay.checkIn) === calendarDayKey(year, month, day);
}

// Returns true on the first day the stay is visible in this month view — either
// the actual check-in day, or day 1 when the stay carries over from a prior month.
function isLabelDay(stay: Stay, year: number, month: number, day: number): boolean {
  if (isStayStart(stay, year, month, day)) return true;
  if (day !== 1) return false;
  const { y: ciY, m: ciM } = isoToYMD(stay.checkIn);
  return ciY < year || (ciY === year && ciM < month);
}

const STAY_PALETTE = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

function stayColor(stayId: string): string {
  let hash = 0;
  for (let i = 0; i < stayId.length; i++) {
    hash = (hash * 31 + stayId.charCodeAt(i)) & 0xffffffff;
  }
  return STAY_PALETTE[Math.abs(hash) % STAY_PALETTE.length];
}

// ─── Stay Form ────────────────────────────────────────────────────────────────

const GUEST_NAME_MAX_LENGTH = 100;

interface StayFormState {
  guestName: string;
  checkInDate: string;
  checkInTime: string;
  checkOutDate: string;
  checkOutTime: string;
  memo: string;
}

const EMPTY_STAY_FORM: StayFormState = {
  guestName: '',
  checkInDate: '',
  checkInTime: '14:00',
  checkOutDate: '',
  checkOutTime: '11:00',
  memo: '',
};

function stayToForm(stay: Stay): StayFormState {
  return {
    guestName: stay.guestName,
    checkInDate: toLocalDateString(stay.checkIn),
    checkInTime: toLocalTimeString(stay.checkIn),
    checkOutDate: toLocalDateString(stay.checkOut),
    checkOutTime: toLocalTimeString(stay.checkOut),
    memo: stay.memo,
  };
}

interface StayFormPanelProps {
  form: StayFormState;
  onChange: (patch: Partial<StayFormState>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  submitting: boolean;
  errors: Record<string, string>;
  isEditing: boolean;
}

function StayFormPanel({
  form,
  onChange,
  onSubmit,
  onCancel,
  onDelete,
  submitting,
  errors,
  isEditing,
}: StayFormPanelProps) {
  const inputClass =
    'w-full rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent';
  const errorClass = 'mt-1 text-xs text-danger';
  const labelClass = 'mb-1 block text-sm font-medium text-text-primary';

  return (
    <div className="rounded-xl border border-border bg-bg-primary p-5">
      <h2 className="mb-4 text-sm font-semibold text-text-primary">
        {isEditing ? 'Edit stay' : 'New stay'}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Check-in */}
        <div>
          <label htmlFor="stay-checkin-date" className={labelClass}>
            Check-in date
          </label>
          <input
            id="stay-checkin-date"
            type="date"
            value={form.checkInDate}
            onChange={(e) => onChange({ checkInDate: e.target.value })}
            className={`${inputClass} ${errors.checkIn ? 'border-danger' : ''}`}
          />
          {errors.checkIn && <p className={errorClass}>{errors.checkIn}</p>}
        </div>
        <div>
          <label htmlFor="stay-checkin-time" className={labelClass}>
            Check-in time
          </label>
          <input
            id="stay-checkin-time"
            type="time"
            value={form.checkInTime}
            onChange={(e) => onChange({ checkInTime: e.target.value })}
            className={inputClass}
          />
        </div>

        {/* Check-out */}
        <div>
          <label htmlFor="stay-checkout-date" className={labelClass}>
            Check-out date
          </label>
          <input
            id="stay-checkout-date"
            type="date"
            value={form.checkOutDate}
            onChange={(e) => onChange({ checkOutDate: e.target.value })}
            className={`${inputClass} ${errors.checkOut ? 'border-danger' : ''}`}
          />
          {errors.checkOut && <p className={errorClass}>{errors.checkOut}</p>}
        </div>
        <div>
          <label htmlFor="stay-checkout-time" className={labelClass}>
            Check-out time
          </label>
          <input
            id="stay-checkout-time"
            type="time"
            value={form.checkOutTime}
            onChange={(e) => onChange({ checkOutTime: e.target.value })}
            className={inputClass}
          />
        </div>

        {/* Guest Name */}
        <div className="sm:col-span-2">
          <label htmlFor="stay-guest-name" className={labelClass}>
            Guest name
          </label>
          <input
            id="stay-guest-name"
            type="text"
            value={form.guestName}
            onChange={(e) => onChange({ guestName: e.target.value })}
            placeholder="e.g. John Smith"
            className={`${inputClass} ${errors.guestName ? 'border-danger' : ''}`}
            maxLength={GUEST_NAME_MAX_LENGTH}
          />
          {errors.guestName && <p className={errorClass}>{errors.guestName}</p>}
        </div>

        {/* Memo */}
        <div className="sm:col-span-2">
          <label htmlFor="stay-memo" className={labelClass}>
            Memo <span className="text-text-secondary">(optional)</span>
          </label>
          <textarea
            id="stay-memo"
            rows={2}
            value={form.memo}
            onChange={(e) => onChange({ memo: e.target.value })}
            placeholder="e.g. Booking reference, special requests…"
            className={`${inputClass} resize-none`}
            maxLength={500}
          />
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-border pt-4">
        {isEditing && onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            disabled={submitting}
            className="rounded-lg px-3 py-2 text-sm text-danger hover:bg-danger/5 focus:outline-none focus:ring-2 focus:ring-danger disabled:opacity-50"
          >
            Delete stay
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-4 py-2 text-sm text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save stay'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Calendar Grid ────────────────────────────────────────────────────────────

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Stable keys for up to 6 leading padding cells (max offset = 6 for Saturday starts)
const PAD_KEYS = ['pad-0', 'pad-1', 'pad-2', 'pad-3', 'pad-4', 'pad-5', 'pad-6'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface CalendarGridProps {
  year: number;
  month: number;
  stays: Stay[];
  onDayClick: (dateStr: string) => void;
  onStayClick: (stay: Stay) => void;
}

function CalendarGrid({ year, month, stays, onDayClick, onStayClick }: CalendarGridProps) {
  const totalDays = daysInMonth(year, month);
  const startOffset = firstDayOfMonth(year, month);
  const today = new Date();
  const todayKey = calendarDayKey(today.getFullYear(), today.getMonth(), today.getDate());

  // Build flat cell list with stable keys, then chunk into 7-cell rows for <tr>s.
  // Indices from Array.from are used only for day/position values, never as React keys.
  type Cell = { key: string; day: number | null };
  const allCells: Cell[] = [
    ...PAD_KEYS.slice(0, startOffset).map((k) => ({ key: k, day: null as null })),
    ...Array.from({ length: totalDays }, (_, i) => ({
      key: calendarDayKey(year, month, i + 1),
      day: i + 1,
    })),
  ];
  while (allCells.length % 7 !== 0) {
    allCells.push({ key: `trail-${DAY_HEADERS[allCells.length % 7]}`, day: null });
  }
  const rowCount = allCells.length / 7;
  const weeks: Cell[][] = Array.from({ length: rowCount }, (_, r) =>
    allCells.slice(r * 7, (r + 1) * 7),
  );

  return (
    <div className="rounded-xl border border-border bg-bg-primary overflow-hidden">
      <table className="w-full table-fixed border-collapse">
        <thead>
          <tr className="border-b border-border">
            {DAY_HEADERS.map((d) => (
              <th
                key={d}
                scope="col"
                className="py-2 text-center text-xs font-medium uppercase tracking-wide text-text-secondary"
              >
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, weekIdx) => {
            const isLastRow = weekIdx === weeks.length - 1;
            return (
              <tr key={week[0].key}>
                {week.map(({ key, day }, colIdx) => {
                  const isLastCol = colIdx === 6;
                  if (day === null) {
                    return (
                      <td
                        key={key}
                        className={[
                          'h-20 border-border bg-bg-surface',
                          !isLastCol && 'border-r',
                          !isLastRow && 'border-b',
                        ].join(' ')}
                      />
                    );
                  }
                  const dayKey = calendarDayKey(year, month, day);
                  const isToday = dayKey === todayKey;
                  const dayStays = stays.filter((s) => stayOverlapsDay(s, year, month, day));
                  return (
                    <td
                      key={key}
                      className={[
                        'relative h-20 p-1.5 border-border align-top',
                        !isLastCol && 'border-r',
                        !isLastRow && 'border-b',
                      ].join(' ')}
                    >
                      {/* Full-cell click target — a real <button> so Biome a11y rules are satisfied.
                          Stay blocks sit at z-10 and intercept their own clicks without stopPropagation. */}
                      <button
                        type="button"
                        className="absolute inset-0 cursor-pointer hover:bg-bg-surface transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent/50"
                        onClick={() => onDayClick(dayKey)}
                        aria-label={`Add stay on ${dayKey}`}
                        tabIndex={-1}
                      />
                      <span
                        className={[
                          'relative z-10 flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
                          isToday ? 'bg-accent text-white' : 'text-text-primary',
                        ].join(' ')}
                      >
                        {day}
                      </span>
                      <div className="relative z-10 mt-0.5 space-y-0.5">
                        {dayStays.map((stay) => (
                          <button
                            key={stay.id}
                            type="button"
                            onClick={() => onStayClick(stay)}
                            className={[
                              'flex h-5 w-full items-center overflow-hidden rounded px-1 text-left text-xs font-medium text-white',
                              'hover:opacity-90 focus:outline-none focus:ring-1 focus:ring-white/50',
                              isStayStart(stay, year, month, day)
                                ? 'rounded-l-full pl-2'
                                : 'rounded-l-none',
                              toLocalDateString(stay.checkOut) === calendarDayKey(year, month, day)
                                ? 'rounded-r-full'
                                : 'rounded-r-none',
                            ].join(' ')}
                            style={{ backgroundColor: stayColor(stay.id) }}
                            title={stay.guestName}
                          >
                            <span className="truncate">
                              {isLabelDay(stay, year, month, day) ? stay.guestName : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── StayCalendar (inner) ─────────────────────────────────────────────────────

interface StayCalendarProps {
  entityId: string;
  buildings: Building[];
}

function StayCalendar({ entityId, buildings }: StayCalendarProps) {
  const queryClient = useQueryClient();
  const now = new Date();

  const [selectedBuildingId, setSelectedBuildingId] = useState(buildings[0]?.id ?? '');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [showForm, setShowForm] = useState(false);
  const [editingStay, setEditingStay] = useState<Stay | null>(null);
  const [form, setForm] = useState<StayFormState>(EMPTY_STAY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const activeBuildingId = selectedBuildingId || buildings[0]?.id || '';

  const staysQuery = useQuery({
    queryKey: ['stays', entityId, activeBuildingId],
    queryFn: () => api.stays.list(entityId, activeBuildingId),
    enabled: !!activeBuildingId,
  });

  const stays = staysQuery.data?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (body: { guestName: string; checkIn: string; checkOut: string; memo: string }) =>
      api.stays.create(entityId, activeBuildingId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stays', entityId, activeBuildingId] });
      closeForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      stayId,
      body,
    }: {
      stayId: string;
      body: Partial<{ guestName: string; checkIn: string; checkOut: string; memo: string }>;
    }) => api.stays.update(entityId, activeBuildingId, stayId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stays', entityId, activeBuildingId] });
      closeForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (stayId: string) => api.stays.delete(entityId, activeBuildingId, stayId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stays', entityId, activeBuildingId] });
      closeForm();
    },
  });

  function openCreate(prefillDate?: string) {
    setEditingStay(null);
    setForm({
      ...EMPTY_STAY_FORM,
      checkInDate: prefillDate ?? '',
      checkOutDate: prefillDate ?? '',
    });
    setFormErrors({});
    setShowForm(true);
  }

  function openEdit(stay: Stay) {
    setEditingStay(stay);
    setForm(stayToForm(stay));
    setFormErrors({});
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingStay(null);
    setFormErrors({});
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};
    const trimmedGuestName = form.guestName.trim();
    if (!trimmedGuestName) {
      errors.guestName = 'Guest name is required.';
    } else if (trimmedGuestName.length > GUEST_NAME_MAX_LENGTH) {
      errors.guestName = `Guest name must be ${GUEST_NAME_MAX_LENGTH} characters or fewer.`;
    }
    if (!form.checkInDate) errors.checkIn = 'Check-in date is required.';
    if (!form.checkOutDate) errors.checkOut = 'Check-out date is required.';
    if (form.checkInDate && form.checkOutDate) {
      const checkIn = new Date(buildIso(form.checkInDate, form.checkInTime));
      const checkOut = new Date(buildIso(form.checkOutDate, form.checkOutTime));
      if (checkOut <= checkIn) errors.checkOut = 'Check-out must be after check-in.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleSubmit() {
    if (!validateForm()) return;
    const body = {
      guestName: form.guestName.trim(),
      checkIn: buildIso(form.checkInDate, form.checkInTime),
      checkOut: buildIso(form.checkOutDate, form.checkOutTime),
      memo: form.memo.trim(),
    };
    if (editingStay) {
      updateMutation.mutate({ stayId: editingStay.id, body });
    } else {
      createMutation.mutate(body);
    }
  }

  function handleDelete() {
    if (!editingStay) return;
    if (confirm('Delete this stay?')) {
      deleteMutation.mutate(editingStay.id);
    }
  }

  function prevMonth() {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
  }

  function nextMonth() {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else setMonth((m) => m + 1);
  }

  function goToToday() {
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  }

  const submitting =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Filter stays to only those visible in current month view
  const { y: viewY, m: viewM } = { y: year, m: month };
  const visibleStays = stays.filter((s) => {
    const { y: ciY, m: ciM } = isoToYMD(s.checkIn);
    const { y: coY, m: coM } = isoToYMD(s.checkOut);
    const startsBefore = ciY < viewY || (ciY === viewY && ciM <= viewM);
    const endsAfter = coY > viewY || (coY === viewY && coM >= viewM);
    return startsBefore && endsAfter;
  });

  return (
    <div>
      {/* Building selector */}
      {buildings.length > 1 && (
        <div className="mb-5">
          <BuildingSearchDropdown
            buildings={buildings}
            selected={selectedBuildingId}
            onChange={setSelectedBuildingId}
            placeholder="Search buildings…"
          />
        </div>
      )}

      {/* Form panel */}
      {showForm && (
        <div className="mb-6">
          <StayFormPanel
            form={form}
            onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            onSubmit={handleSubmit}
            onCancel={closeForm}
            onDelete={editingStay ? handleDelete : undefined}
            submitting={submitting}
            errors={formErrors}
            isEditing={!!editingStay}
          />
        </div>
      )}

      {/* Calendar header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={prevMonth}
            className="rounded-lg p-2 text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Previous month"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h2 className="min-w-[160px] text-center text-base font-semibold text-text-primary">
            {MONTH_NAMES[month]} {year}
          </h2>
          <button
            type="button"
            onClick={nextMonth}
            className="rounded-lg p-2 text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Next month"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goToToday}
            className="ml-2 rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary ring-1 ring-border hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Today
          </button>
        </div>

        {!showForm && (
          <button
            type="button"
            onClick={() => openCreate()}
            className="w-full rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50 sm:w-auto"
          >
            + New stay
          </button>
        )}
      </div>

      {/* Calendar grid */}
      {staysQuery.isLoading ? (
        <div className="h-96 animate-pulse rounded-xl border border-border bg-bg-primary" />
      ) : (
        <CalendarGrid
          year={year}
          month={month}
          stays={visibleStays}
          onDayClick={(dateStr) => {
            if (!showForm) {
              openCreate(dateStr);
            } else if (!editingStay) {
              setForm((prev) => {
                if (!prev.checkInDate || dateStr <= prev.checkInDate) {
                  return {
                    ...prev,
                    checkInDate: dateStr,
                    checkOutDate: prev.checkOutDate >= dateStr ? prev.checkOutDate : dateStr,
                  };
                }
                return { ...prev, checkOutDate: dateStr };
              });
            }
          }}
          onStayClick={(stay) => {
            openEdit(stay);
          }}
        />
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function StayCalendarPage() {
  const { user } = useAuthContext();
  const entityId = user!.entityId!;

  const { data: entityRes } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => api.get<Entity>(`/api/entities/${entityId}`),
  });
  const entity = entityRes?.data;
  const buildings = entity?.buildings ?? [];
  const hasAddon = entity?.addOns?.stayCalendar === true;

  return (
    <EnterpriseLayout title="Stay Calendar">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-text-primary">Stay Calendar</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Track guest bookings and detect occupancy outside booked stays.
          </p>
        </div>

        {!entity ? (
          <div className="h-96 animate-pulse rounded-xl border border-border bg-bg-primary" />
        ) : !hasAddon ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-bg-elevated">
              <svg
                className="h-7 w-7 text-text-secondary"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
                <path d="M12 14l1.5 1.5L16 13" strokeWidth="1.5" />
              </svg>
            </div>
            <h2 className="mb-2 text-base font-semibold text-text-primary">Stay Calendar</h2>
            <p className="max-w-sm text-sm text-text-secondary">
              This feature requires the Stay Calendar add-on. Contact your Intecular account manager
              to enable it.
            </p>
          </div>
        ) : (
          <StayCalendar entityId={entityId} buildings={buildings} />
        )}
      </div>
    </EnterpriseLayout>
  );
}
